/**
 * The cookie answer, kept where the current app keeps it (localStorage
 * `cookie-consent`), so a visitor who has answered there has answered here.
 * The page's head defines window.loadAnalytics and calls it on its own when the
 * answer is 'granted' (src/app/layout.tsx).
 */
const STORAGE_KEY = "cookie-consent";
export const GRANTED = "granted";
export const DENIED = "denied";

declare global {
  interface Window {
    GOOGLE_ANALYTICS_ID?: string | null;
    loadAnalytics?: (() => void) & { loaded?: boolean };
    gtag?: (...args: unknown[]) => void;
    dataLayer?: unknown[];
  }
}

export const readConsent = (): string | null => {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
};

const keepConsent = (value: string) => {
  try {
    localStorage.setItem(STORAGE_KEY, value);
  } catch {
    // storage blocked: the choice holds for this visit, and is asked again next time
  }
};

const deleteAnalyticsCookies = () => {
  const names = document.cookie
    .split(";")
    .map((cookie) => cookie.split("=")[0].trim())
    .filter((name) => name === "_ga" || name.startsWith("_ga_"));
  const parts = window.location.hostname.split(".");
  const domains = [""];
  for (let i = 0; i < parts.length - 1; i++) {
    domains.push(`; domain=.${parts.slice(i).join(".")}`);
  }
  names.forEach((name) =>
    domains.forEach((domain) => {
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/${domain}`;
    }),
  );
};

export const grantConsent = () => {
  keepConsent(GRANTED);
  window.loadAnalytics?.();
};

export const refuseConsent = () => {
  keepConsent(DENIED);
  const id = window.GOOGLE_ANALYTICS_ID;
  if (id && window.loadAnalytics?.loaded) {
    (window as unknown as Record<string, unknown>)[`ga-disable-${id}`] = true;
    window.gtag?.("consent", "update", { analytics_storage: "denied" });
  }
  deleteAnalyticsCookies();
};

export const needsConsentAnswer = () => {
  const consent = readConsent();
  return consent !== GRANTED && consent !== DENIED;
};
